import { SlidersHorizontal, X } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useUpdateExchangeOffsets, type IExchangeOffsets } from "./UseUpdateExchangesOffsets";
import { useExchangeOffsets } from "./useExchangeOffsets";

type TOffsetForm = Record<keyof IExchangeOffsets, string>;

const INITIAL_FORM: TOffsetForm = {
  binanceSellOffset: "0.10",
  binanceBuyOffset: "0.10",
  bybitSellOffset: "0.10",
  bybitBuyOffset: "0.10",
  mexcSellOffset: "0.10",
  mexcBuyOffset: "0.10",
  bitgetSellOffset: "0.10",
  bitgetBuyOffset: "0.10",
  gateSellOffset: "0.10",
  gateBuyOffset: "0.10",
};

export const BallonEditPrice = () => {
  const { data, isLoading } = useExchangeOffsets();
  const { mutate: updateOffsets, isPending: updatingOffsets } = useUpdateExchangeOffsets();

  const [editingOffsets, setEditingOffsets] = useState(false);
  const [form, setForm] = useState<TOffsetForm>(INITIAL_FORM);

  useEffect(() => {
    if (data) {
      setForm({
        binanceSellOffset: String(data.binanceSellOffset),
        binanceBuyOffset: String(data.binanceBuyOffset),
        bybitSellOffset: String(data.bybitSellOffset),
        bybitBuyOffset: String(data.bybitBuyOffset),
        mexcSellOffset: String(data.mexcSellOffset),
        mexcBuyOffset: String(data.mexcBuyOffset),
        bitgetSellOffset: String(data.bitgetSellOffset),
        bitgetBuyOffset: String(data.bitgetBuyOffset),
        gateSellOffset: String(data.gateSellOffset),
        gateBuyOffset: String(data.gateBuyOffset),
      });
    }
  }, [data]);

  const handleOpen = () => {
    if (!editingOffsets) setEditingOffsets(true);
  };

  const handleClose = () => {
    if (!updatingOffsets) setEditingOffsets(false);
  };

  const handleChange = (field: keyof IExchangeOffsets, value: string) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const parseNumber = (value: string) => Number(value.replace(",", "."));

  const handleSave = () => {
    const payload: IExchangeOffsets = {
      binanceSellOffset: parseNumber(form.binanceSellOffset),
      binanceBuyOffset: parseNumber(form.binanceBuyOffset),
      bybitSellOffset: parseNumber(form.bybitSellOffset),
      bybitBuyOffset: parseNumber(form.bybitBuyOffset),
      mexcSellOffset: parseNumber(form.mexcSellOffset),
      mexcBuyOffset: parseNumber(form.mexcBuyOffset),
      bitgetSellOffset: parseNumber(form.bitgetSellOffset),
      bitgetBuyOffset: parseNumber(form.bitgetBuyOffset),
      gateSellOffset: parseNumber(form.gateSellOffset),
      gateBuyOffset: parseNumber(form.gateBuyOffset),
    };

    const hasInvalid = Object.values(payload).some((value) => !Number.isFinite(value));

    if (hasInvalid) return;

    updateOffsets(payload, {
      onSuccess: () => setEditingOffsets(false),
    });
  };

  const summary = isLoading
    ? "Carregando..."
    : `BINANCE___ SELL: ${form.binanceSellOffset} BUY: ${form.binanceBuyOffset} | BYBIT_____ SELL: ${form.bybitSellOffset} BUY: ${form.bybitBuyOffset} | MEXC______ SELL: ${form.mexcSellOffset} BUY: ${form.mexcBuyOffset} | BITGET____ SELL: ${form.bitgetSellOffset} BUY: ${form.bitgetBuyOffset} | GATE______ SELL: ${form.gateSellOffset} BUY: ${form.gateBuyOffset} `;

  const renderSection = (
    title: string,
    sellField: keyof IExchangeOffsets,
    buyField: keyof IExchangeOffsets,
  ) => (
    <div className="rounded-8 border border-gray-200 p-2">
      <h6 className="mb-2 font-semibold text-blue-700">{title}</h6>

      <div className="flex flex-col gap-2">
        <label className="flex items-center justify-between gap-2">
          <h6>SELL OFFSET</h6>
          <input
            value={form[sellField]}
            onChange={(e) => handleChange(sellField, e.target.value)}
            className="w-32 rounded border border-gray-300 px-1 py-0.5 text-right"
          />
        </label>

        <label className="flex items-center justify-between gap-2">
          <h6>BUY OFFSET</h6>
          <input
            value={form[buyField]}
            onChange={(e) => handleChange(buyField, e.target.value)}
            className="w-32 rounded border border-gray-300 px-1 py-0.5 text-right"
          />
        </label>
      </div>
    </div>
  );

  return (
    <div className="fixed bottom-4 left-4 z-50 flex max-w-[460px] flex-col gap-2 rounded-10 bg-gray-50 p-2 text-32 text-gray-700 shadow-lg">
      {!editingOffsets ? (
        <button
          type="button"
          onClick={handleOpen}
          disabled={isLoading}
          className="flex items-center gap-2 rounded-6 bg-blue-600 p-2 font-medium text-white hover:bg-blue-700 disabled:opacity-60"
        >
          <SlidersHorizontal size={18} />

          <div className="flex flex-col gap-2 text-left leading-tight md:w-28">
            <h6>Editar offsets</h6>

            {/* Abaixo de md: esconde os preços enquanto o balão estiver fechado */}
            <span className="text-16 md:hidden">...</span>

            {/* A partir de md: exibe normalmente os preços */}
            <span className="hidden text-16 md:block">{summary}</span>
          </div>
        </button>
      ) : (
        <>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 font-medium text-blue-700">
              <SlidersHorizontal size={18} />
              <h6>Editar offsets por corretora</h6>
            </div>

            <button
              type="button"
              onClick={handleClose}
              className="rounded-full p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-800"
            >
              <X size={16} weight="bold" />
            </button>
          </div>

          <div className="mt-1 flex flex-col gap-2">
            {renderSection("Binance", "binanceSellOffset", "binanceBuyOffset")}
            {renderSection("Bybit", "bybitSellOffset", "bybitBuyOffset")}
            {renderSection("Mexc", "mexcSellOffset", "mexcBuyOffset")}
            {renderSection("Bitget", "bitgetSellOffset", "bitgetBuyOffset")}
            {renderSection("Gate", "gateSellOffset", "gateBuyOffset")}
          </div>

          <button
            type="button"
            onClick={handleSave}
            disabled={updatingOffsets}
            className="mt-1 flex items-center justify-center gap-2 rounded-6 bg-blue-600 px-2 py-1 text-20 font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {updatingOffsets ? "Salvando..." : "Salvar offsets"}
          </button>
        </>
      )}
    </div>
  );
};
